// Warianty ekranów z maskotką.
//  • Powitanie („Zaczynamy!”, 3 wersje): każdy kolejny pokaz bierze następny wariant (`createVariants`). Licznik jest w pamięci przeglądarki
//    (przeżywa odświeżenie strony); gdy jej brak (tryb prywatny), losujemy. Wariant jest stały do następnego `advance`.
//  • Ekran główny (5 wersji): JEDNA wersja na cały dzień (`dailyVariant`) — odświeżenie strony ani powrót z lekcji jej nie zmieniają;
//    następnego dnia jest kolejna. Wybór wynika tylko z daty i profilu, więc nie potrzebuje pamięci i na każdym urządzeniu dziecka jest taki sam.
import { daysBetween } from './dates.ts';
import type { KeyValueStore } from './progress.ts';

export type VariantKind = 'welcome';

/**
 * Wariant (0…count-1) ekranu głównego na dany dzień (`date` = RRRR-MM-DD). Kolejne dni dają kolejne warianty (nigdy dwa dni pod rząd ten sam, gdy jest ich więcej niż jeden),
 * a `seed` (identyfikator profilu) przesuwa początek cyklu, żeby rodzeństwo na jednym tablecie nie widziało dokładnie tego samego powitania.
 */
export function dailyVariant(date: string, count: number, seed = ''): number {
  if (count <= 1) return 0;
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) % 1_000_003;
  const day = daysBetween('1970-01-01', date);
  return (((day + h) % count) + count) % count;
}

export interface Variants {
  /** Bieżący wariant (0…count-1). */
  current(kind: VariantKind, count: number): number;
  /** Przechodzi do następnego wariantu (wołane raz na „wejście” na ekran). */
  advance(kind: VariantKind, count: number): number;
}

const KEY = (kind: VariantKind): string => `naative.variant.v1.${kind}`;

export function createVariants(store: KeyValueStore | null, random: () => number = Math.random): Variants {
  const shown = new Map<VariantKind, number>();
  const read = (kind: VariantKind): number | null => {
    try {
      const raw = store?.getItem(KEY(kind));
      return typeof raw === 'string' && /^\d{1,9}$/.test(raw) ? Number(raw) : null;
    } catch {
      return null;
    }
  };
  const write = (kind: VariantKind, n: number): void => {
    try {
      store?.setItem(KEY(kind), String(n));
    } catch {
      /* pamięć niedostępna: następnym razem wylosujemy */
    }
  };
  const api: Variants = {
    advance(kind, count) {
      if (count <= 1) {
        shown.set(kind, 0);
        return 0;
      }
      const last = read(kind);
      let next = last === null ? Math.floor(random() * count) : (last + 1) % count;
      // bez pamięci losujemy, ale nie dwa razy to samo pod rząd w jednej sesji
      const prev = shown.get(kind);
      if (last === null && prev !== undefined && next === prev) next = (next + 1) % count;
      shown.set(kind, next);
      write(kind, next);
      return next;
    },
    current(kind, count) {
      if (count <= 1) return 0;
      const n = shown.get(kind);
      return n === undefined ? api.advance(kind, count) : n % count;
    },
  };
  return api;
}
